import "./custom_hooks";
import React from "react";
import PropTypes from "prop-types";
import * as constants from './constants';
import { setRpRefFactory, getSiteDataKey,
    pushExternalModules } from "./LinoUtils";
import { RegisterImportPool, getExReady, Component } from "./Base";

import(/* webpackChunkName: "AppCSS" */"./AppCSS");

let ex; const exModulePromises = ex = {
    rdc: import(/* webpackChunkName: "reactDom_App" */"react-dom/client"),
    rrd: import(/* webpackChunkName: "reactRouterDom_App" */"react-router-dom"),
    queryString: import(/* webpackChunkName: "queryString_App" */"query-string"),
    weakKey: import(/* webpackChunkName: "weakKey_App" */"weak-key"),
    ReconnectingWebSocket: import(/* webpackChunkName: "ReconnectingWebSocket_App" */"reconnecting-websocket"),
    prAPI: import(/* webpackChunkName: "prAPI_App" */"primereact/api"),
    prButton: import(/* webpackChunkName: "prButton_App" */"primereact/button"),
    prLocale: import(/* webpackChunkName: "prLocale_App" */"primelocale"),
    prToast: import(/* webpackChunkName: "prToast_App" */"primereact/toast"),
    nc: import(/* webpackChunkName: "NavigationControl_App" */"./NavigationControl"),
    sc: import(/* webpackChunkName: "SiteContext_App" */"./SiteContext"),
    i18n: import(/* webpackChunkName: "i18n_App" */"./i18n"),
};RegisterImportPool(exModulePromises);

const OPEN_IN_APP_BANNER_DISMISSED_KEY = 'lino-open-in-app-banner-dismissed';
const FIX_APP_LINKS_CTA_DISMISSED_KEY = 'lino-fix-app-links-cta-dismissed';


/**
 *
 * Renders a HashRouter and renders the :js:class:`App` inside
 * upon when the HashRouter is available to the DOM.
 */
function LinoRouter(props) {
    const navigate = props.RRD.useNavigate();
    const location = props.RRD.useLocation();
    return <App navigate={navigate} location={location}/>
}

LinoRouter.propTypes = {
    RRD: PropTypes.shape({
        useNavigate: PropTypes.func.isRequired,
        useLocation: PropTypes.func.isRequired,
    }).isRequired,
};

export {LinoRouter};


/**
 * @typedef {Object} ServerErrorProps
 */
const ServerErrorProps = {
    APP: PropTypes.object.isRequired,
}

/**
 * Component to render error message on status_code >= 500
 *
 * @param {ServerErrorProps} props
 */
function InternalServerError(props) {
    const localEx = getExReady(ex, ["prButton", "i18n"], (mods) => {
        mods.i18n = mods.i18n.default;
    });
    return !localEx.ready ? null : <div style={{textAlign: 'center'}}>
        <p>There was a problem on the server. If the problem persists, contact your site maintainer.</p>
        <localEx.prButton.Button label={localEx.i18n.t('Reinitialize')}
            // onClick={() => window.location.reload()}/>
            onClick={() => props.APP.reload()}/>
    </div>
}
export { InternalServerError }

InternalServerError.propTypes = ServerErrorProps;


/**
 *
 * The app class.
 *
 * Subclasses :class:`Component`
 *
 */
class App extends Component {
    static requiredModules = ["nc", "sc", "prToast", "ReconnectingWebSocket",
        "queryString", "i18n", "prAPI", "prLocale"];
    static iPool = ex;

    /** Keeps a reference to the singleton :js:class:`DialogFactory` instance. */
    dialogFactory = null;
    /** Keeps a reference to a `Toast <https://www.primefaces.org/primereact-v6/#/toast>`__ object. */
    toast = null;
    /** Keeps a reference to a :js:class:`Context` object (and is the :js:class:`RootURLContext` 's controller.) */
    URLContext = null;
    /**
     *
     */
    static propTypes = {
        location: PropTypes.object.isRequired,
        navigate: PropTypes.func.isRequired,
    }

    _skipDefaultSetReady = true;

    async prepare() {
        this.ex.queryString = this.ex.queryString.default;
        const { TransInit } = this.ex.i18n;
        this.ex.i18n = this.ex.i18n.default;
        const params = this.ex.queryString.parse(this.location.search.slice(1));
        const onTransInit = async (ti) => {
            if (params[constants.URL_PARAM_USER_LANGUAGE])
                await ti.changeLanguage(params[constants.URL_PARAM_USER_LANGUAGE]);
            if (params[constants.URL_PARAM_SUBST_USER]) {
                await this.URLContext.history.replaceByType(
                    {[constants.URL_PARAM_SUBST_USER]: params[constants.URL_PARAM_SUBST_USER]},
                    constants.PARAM_TYPE_WINDOW, false, true);
            }
            if (["light", "dark"].includes(params[constants.URL_PARAM_COLOR_SCHEME])) {
                await this.URLContext.history.replaceByType(
                    {[constants.URL_PARAM_COLOR_SCHEME]: params[constants.URL_PARAM_COLOR_SCHEME]},
                    constants.PARAM_TYPE_WINDOW, false, true);
            }

            this.storageName = `lino-${window.Lino.site_name}-json-cache`;
            const request = window.indexedDB.open(this.storageName, 1);
            request.onsuccess = (event) => {
                this.cacheDB = event.target.result;
                this.setReady();
            }
            request.onupgradeneeded = (event) => {
                event.target.result.createObjectStore(this.storageName);
            }

            /**
             * CAUTION: Do NOT remove the translations below
             * Not useful at runtime, but they are put to the
             * *translation.json files and are used in other
             * translations.
             */
            this.ex.i18n.t("whiteSpace", " ");
            this.ex.i18n.t("colonSpaced", ":$t(whiteSpace)");
        }
        this.URLContext = new this.ex.nc.Context({APP: this,
            rs: params.rs,
            next: (ucc) => TransInit(ucc, onTransInit)});
    }

    /**
     * @param {Object} [props] see: :js:attr:`propTypes`.
     */
    constructor(props) {
        super(props);
        this.location = props.location;
        this.navigate = props.navigate;
        /**
         * Keeps references to :js:class:`ActionHandler` (s).
         * (internally known as requesting_panel as rp(s))
         */
        this.rps = {};
        /** Current state of the :js:class:`App`. */
        this.state = {
            ...this.state,
            site_loading: true,
            site_data: null,
            menu_data: null,
            user_settings: null,
            initProgress: 0,
            showOpenInAppBanner: false,
            showFixAppLinksCta: false,
            serverError: false,

            WS: false, // Websocket status
            children: null,
        };

        this.data = {
            miStore: [],
            themeName: 'default', // `whitewall`, `default`

            selectedLanguage: null,
            scroll: {},
            scrollIndex: [],
            zoomHandles: [],
        }
        this.setRpRef = setRpRefFactory(this.rps);

        this.createAccount = this.createAccount.bind(this);
        this.fetch_user_settings = this.fetch_user_settings.bind(this);
        this.fetch_site_data = this.fetch_site_data.bind(this);
        // this.handleVerification = this.handleVerification.bind(this);
        this.handleZoom = this.handleZoom.bind(this);
        this.interceptBrowserBF = this.interceptBrowserBF.bind(this);
        this.interceptBrowserReload = this.interceptBrowserReload.bind(this);
        this.messageInterceptor = this.messageInterceptor.bind(this);
        this.onHrefClick = this.onHrefClick.bind(this);
        this.onMysettings = this.onMysettings.bind(this);
        this.onReady = this.onReady.bind(this);
        this.registerHandle = this.registerHandle.bind(this);
        this.unsetLoadMask = this.unsetLoadMask.bind(this);
        this.reset = this.reset.bind(this);
        this.setLoadMask = this.setLoadMask.bind(this);
        this.setServerError = this.setServerError.bind(this);
        this.unregisterHandle = this.unregisterHandle.bind(this);
        this.getSettings = this.getSettings.bind(this);
        this.setSettings = this.setSettings.bind(this);
        this.setInitProgress = this.setInitProgress.bind(this);

        this.onSignOutIn = this.onSignOutIn.bind(this);
        // this.onSignIn = this.onSignIn.bind(this);

        this.runAction = this.runAction.bind(this);

        this.notification_web_socket = this.notification_web_socket.bind(this);
        this.push = this.push.bind(this);
        this.navigateFromNative = this.navigateFromNative.bind(this);
        this.openInAppFromBrowser = this.openInAppFromBrowser.bind(this);
        this.dismissOpenInAppBanner = this.dismissOpenInAppBanner.bind(this);
        this.openAndroidAppLinkSettings = this.openAndroidAppLinkSettings.bind(this);
        this.dismissFixAppLinksCta = this.dismissFixAppLinksCta.bind(this);
        this.notifyAppLinkBannerLayoutChanged = this.notifyAppLinkBannerLayoutChanged.bind(this);

        window.App = this;
    }

    /**
     * Does what componentDidMount does.
     */
    onReady() {
        this.setState({
            initProgress: 5,
            children: <this.ex.sc.LinoProgressBar loading={true} progress={5}/>,
        });
        // Reset language in the case of App.reload calls (by detecting
        // if the URLContext.value.ul is empty)
        this.reset(!Object.prototype.hasOwnProperty.call(
            this.URLContext.value, constants.URL_PARAM_USER_LANGUAGE));
        this.setTheme(this);
        window.addEventListener('message', this.messageInterceptor);
        window.addEventListener('click', this.onHrefClick);
        window.onbeforeunload = this.interceptBrowserReload;
        window.onpopstate = this.interceptBrowserBF;
    }

    componentWillUnmount() {
        this.cacheDB.close();
        window.removeEventListener('message', this.messageInterceptor);
        window.removeEventListener('click', this.onHrefClick);
    }

    /**
     * Navigate from native Android notification.
     * Parses hash URL and uses React Router navigation instead of full page reload.
     * 
     * @param {string} url - Full URL with hash (e.g., "https://site.com/#/api/trading/MyVoucherApprovals/")
     */
    async navigateFromNative(url) {
        constants.debugMessage("App.navigateFromNative called with URL:", url);
        if (!this.URLContext || !this.URLContext.actionHandler) {
            console.warn('navigateFromNative called before app initialization, falling back to location.href');
            window.location.href = url;
            return;
        }

        try {
            // Extract hash fragment
            let hashUrl = url;
            if (url.includes('#')) {
                hashUrl = url.split('#')[1];
            } else if (hashUrl.startsWith("http")) {
                console.warn("No hashpath found replacing react HashRouter");
                window.location.href = hashUrl;
                return;
            }

            // Split into path and search params
            let [pathname, search] = hashUrl.split('?');
            if (search === undefined) search = '';

            // Use ActionHandler's parser to parse search params
            const params = this.URLContext.actionHandler.parser.parseShallow(search);
            constants.debugMessage("navigateFromNative: Parsed pathname:", pathname, "params:", params);
            const su_id = params[constants.URL_PARAM_SUBST_USER];

            let navigated = false;
            
            if (this.URLContext.filled(su_id) && su_id !== this.URLContext.value[constants.URL_PARAM_SUBST_USER]) {
                constants.debugMessage("Switching substitute user from", this.URLContext.value[constants.URL_PARAM_SUBST_USER], "to", su_id);
                this.navigate(hashUrl);
                navigated = true;
                await this.URLContext.history.replaceByType(
                    {[constants.URL_PARAM_SUBST_USER]: su_id},
                    constants.PARAM_TYPE_WINDOW, false, true);
                await this.reset();
            }

            if (!this.URLContext.filled(su_id) && this.URLContext.filled(this.URLContext.value[constants.URL_PARAM_SUBST_USER])) {
                constants.debugMessage("Clearing substitute user", this.URLContext.value[constants.URL_PARAM_SUBST_USER]);
                this.navigate(hashUrl);
                navigated = true;
                await this.URLContext.history.replaceByType(
                    {[constants.URL_PARAM_SUBST_USER]: undefined},
                    constants.PARAM_TYPE_WINDOW, false, true);
                await this.reset();
            }

            if (!navigated) {
                if (pathname === this.URLContext.value.path) {
                    this.URLContext.actionHandler.refreshChildren([this.URLContext]);
                } else {
                    // Navigate using history API
                    this.URLContext.history.pushPath({
                        pathname: pathname,
                        params: params
                    });
                }
            }

            constants.debugMessage('navigateFromNative: Navigated to', pathname, 'with params', params);
        } catch (error) {
            console.error('navigateFromNative: Error parsing URL', url, error);
            // Fallback to full page reload
            window.location.href = url;
        }
    }

    shouldShowOpenInAppBanner(siteData = null) {
        const options = this.getAppLinkOptions(siteData);
        const isAndroidBrowser = /Android/i.test(navigator.userAgent || '');
        if (!options.show_open_in_app_banner || !options.android_intent_package) {
            return false;
        }
        return isAndroidBrowser
            && !constants.isAndroidWebView()
            && window.localStorage.getItem(OPEN_IN_APP_BANNER_DISMISSED_KEY) !== '1';
    }

    shouldShowFixAppLinksCta(siteData = null) {
        const options = this.getAppLinkOptions(siteData);
        if (!options.show_fix_settings_cta) {
            return false;
        }
        return constants.isAndroidWebView()
            && window.localStorage.getItem(FIX_APP_LINKS_CTA_DISMISSED_KEY) !== '1';
    }

    getAppLinkOptions(siteData = null) {
        const source = siteData || this.state.site_data;
        if (!source || !source.app_link_options) {
            return {};
        }
        return source.app_link_options;
    }

    buildIntentUrlForCurrentPage() {
        try {
            const options = this.getAppLinkOptions();
            const packageName = options.android_intent_package;
            if (!packageName) {
                return null;
            }
            const intentScheme = options.android_intent_scheme || 'https';
            const url = new URL(window.location.href);
            const path = `${url.pathname}${url.search}${url.hash}`;
            const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
            const fullPath = normalizedPath ? `/${normalizedPath}` : '';
            const fallback = encodeURIComponent(window.location.href);
            return `intent://${url.host}${fullPath}#Intent;scheme=${intentScheme};package=${packageName};S.browser_fallback_url=${fallback};end`;
        } catch (error) {
            console.warn('Failed to build intent URL for open-in-app fallback', error);
            return null;
        }
    }

    openInAppFromBrowser() {
        const intentUrl = this.buildIntentUrlForCurrentPage();
        if (!intentUrl) {
            return;
        }
        window.location.href = intentUrl;
    }

    notifyAppLinkBannerLayoutChanged() {
        window.requestAnimationFrame(() => {
            window.dispatchEvent(new Event('l-app-link-banner-changed'));
        });
    }

    dismissOpenInAppBanner() {
        window.localStorage.setItem(OPEN_IN_APP_BANNER_DISMISSED_KEY, '1');
        this.setState({showOpenInAppBanner: false}, this.notifyAppLinkBannerLayoutChanged);
    }

    openAndroidAppLinkSettings() {
        try {
            window.AndroidBridge?.openAppLinkSettings?.();
        } catch (error) {
            console.error('Failed to open Android app link settings', error);
        }
    }

    dismissFixAppLinksCta() {
        window.localStorage.setItem(FIX_APP_LINKS_CTA_DISMISSED_KEY, '1');
        this.setState({showFixAppLinksCta: false}, this.notifyAppLinkBannerLayoutChanged);
    }

    async syncAndroidColorSchemeToWindowGlobals() {
        if (!constants.isAndroidWebView()) {
            return;
        }
        try {
            const colorScheme = window.AndroidBridge?.getEffectiveColorScheme?.();
            if (["light", "dark"].includes(colorScheme)) {
                await this.URLContext.history.replaceByType(
                    {[constants.URL_PARAM_COLOR_SCHEME]: colorScheme},
                    constants.PARAM_TYPE_WINDOW,
                    false,
                    true,
                );
            }
        } catch (error) {
            console.error("Failed to sync Android color scheme", error);
        }
    }

    /**
     * Executes when the browser's reload button is clicked.
     *
     * Used to check and prevent modified data loss.
     *
     * @param [event] `BeforeUnloadEvent <https://developer.mozilla.org/en-US/docs/Web/API/BeforeUnloadEvent>`__
     */
    interceptBrowserReload(event) {
        if (this.URLContext.isModified()) {
            event.preventDefault();
            event.returnValue = true;
        }
    }

    /**
     * Executes when the browser detect a change in the History state.
     *
     * Used to check and prevent modified data loss.
     *
     * @param [event] `PopStateEvent <https://developer.mozilla.org/en-US/docs/Web/API/PopStateEvent>`__
     */
    async interceptBrowserBF(event) {
        const [pathname, search] = document.URL.split('#')[1].split('?'),
            params = this.ex.queryString.parse(search),
            { URLContext } = this;
        if (pathname === URLContext.value.path) return;
        const reset = async () => {
            this.setState({serverError: false});
            await this.reset();
        }
        const pushPath = async (options) => {
            await URLContext.history.pushPath(options);
            if (this.state.serverError) {
                await reset()
            }
        }
        if (URLContext.filled(params.rs)) {
            if (URLContext.history.has(params.rs)) {
                await URLContext.history.load({rs: params.rs, lazy: true})
                if (this.state.serverError) {
                    await reset();
                }
            }
            else {
                // URLContext.history.pushPath({
                //     pathname: pathname, params: params, lazy: true});
                await pushPath({pathname: pathname, params: params, lazy: true});
            }
        } else {
            params.rs = URLContext.newSlug();
            this.navigate(
                URLContext.makePath({path: pathname, ...params}),
                {replace: true});
            console.warn("TODO: Could there be a race condition due to the async nature of navigate and pushPath? If so, how to fix it?")
            // URLContext.history.pushPath({
            //     pathname: pathname, params: params, lazy: true});
            await pushPath({pathname: pathname, params: params, lazy: true});
        }
    }

    registerHandle(handleType, handle) {
        this.data[`${handleType}Handles`].push(handle);
    }

    unregisterHandle(handleType, handle) {
        let i = this.data[`${handleType}Handles`].indexOf(handle);
        if (i > -1) {
            this.data[`${handleType}Handles`].splice(i, 1);
        }
    }

    handleZoom() {
        if (!this.tbContainer) return;
        let tbBottom = this.tbContainer.getBoundingClientRect().bottom;
        function zoomFire(App) {
            if (!App.tbContainer) {
                clearInterval(App.state.zoomHandlerID);
                return;
            }
            let tbBottomCur = App.tbContainer.getBoundingClientRect().bottom;
            if (tbBottomCur == tbBottom) return;
            tbBottom = tbBottomCur;
            Object.values(App.data.zoomHandles).forEach(handle => handle());
        }
        this.state.zoomHandlerID = setInterval(zoomFire, 300, this);
    }

    onHrefClick(event) {
        if (event.ctrlKey
            && event.target.href && event.target.href.startsWith('javascript')) {
            event.preventDefault();
            let action_param = JSON.parse(event.target.href.split('runAction(')[1].slice(0, -1));
            Object.assign(action_param, {clickCatch: true});
            this.runAction(action_param);
        }
    }

    /**
     * Sets a input blocking load mask to the Window.
     */
    setLoadMask() {
        this.setState({loadMask: true});
        // if (document.activeElement.tagName !== 'body')
        //     this.activeElement = document.activeElement;
        // if (this.activeElement) this.activeElement.blur();
    }
    /**
     * Removes the load mask created by :js:meth:`~App.setLoadMask`.
     */
    unsetLoadMask() {
        this.setState({loadMask: false});
        // if (this.activeElement) this.activeElement.focus();
    }

    setTheme(app) {
        if (app.data.themeName === 'whitewall') {
            document.querySelector('body').classList.add('l-whitewall-body-container');
            this.setState({staticMenuInactive: true});
        }
    }

    messageInterceptor(e) {
        if (e.data === "ArrowsTaken" && !this.data.arrowsTaken)
            this.data.arrowsTaken = true;
        else if (e.data === "ArrowsReleased" && this.data.arrowsTaken)
            this.data.arrowsTaken = false;
    }

    setInitProgress(progress) {
        const boundedProgress = Math.max(0, Math.min(100, progress));
        this.setState((prevState) => {
            const next = {initProgress: boundedProgress};
            if (prevState.site_loading) {
                next.children = <this.ex.sc.LinoProgressBar loading={true} progress={boundedProgress}/>;
            }
            return next;
        });
    }

    updatePushSubscription(subscription) {
        // Disabled in favour of notify.Plugin.get_head_lines
        return;
        if (this.state.site_data
            && this.state.site_data.use_push_api) {
            if (!subscription) {
                if ('serviceWorker' in navigator) {
                    navigator.serviceWorker.ready.then((reg) => {
                        reg.pushManager.getSubscription().then(sub => {
                            if (sub !== null) {
                                this.updatePushSubscription(sub);
                            }
                        });
                    });
                }
            }
            else {
                // let lang = document.documentElement.lang || document.documentElement.getAttribute('language');
                let ajax_query = {
                    lang: navigator.userLanguage || navigator.language,
                    userAgent: navigator.userAgent,
                    sub: JSON.stringify(subscription),
                }
                this.URLContext.actionHandler.silentFetch({path:
                    `pushsubscription?${this.ex.queryString.stringify(ajax_query)}`});
            }
        }
    }

    onMysettings(event) {
        let runable = {actorId: "users.Me", action_full_name: "users.Me.detail",
            [constants.URL_PARAM_SELECTED]: [this.state.user_settings.user_id]}
        if (event.ctrlKey) {
            runable.clickCatch = true;
        }
        this.runAction(runable);
    }

    async onSignOutIn(event) {
        if (!this.state.user_settings.logged_in)
            this.runAction({
                actorId: "about.About",
                action_full_name: this.URLContext.actionHandler.findUniqueAction("sign_in").full_name})
        else {
            this.setLoadMask();
            // clear substitute user
            await this.URLContext.history.replaceByType(
                {[constants.URL_PARAM_SUBST_USER]: undefined},
                constants.PARAM_TYPE_WINDOW, false, true);
            
            // Helper to perform logout after optional Android unregister
            const performLogout = async () => {
                await this.URLContext.actionHandler.silentFetch({path:"auth"});
                this.webSocketBridge && this.webSocketBridge.close();
                this.navigate('/');
                await this.reset();
                this.unsetLoadMask();
            };
            
            if (constants.isAndroidWebView()) {
                console.log('Unregistering Android device from notifications');

                // CRITICAL: Wait for unregister to complete before logout
                // to avoid race condition (logout clears session before unregister completes)
                const unregisterPromise = new Promise((resolve) => {
                    let resolved = false;  // Guard against multiple resolve attempts
                    
                    const timeout = setTimeout(() => {
                        if (!resolved) {
                            resolved = true;
                            console.warn('Android unregister timed out after 5 seconds, proceeding with logout');
                            resolve(false);
                        }
                    }, 5000);
                    
                    window.Android = window.Android || {};
                    window.Android.onDeviceUnregistered = function (success) {
                        if (!resolved) {
                            resolved = true;
                            clearTimeout(timeout);
                            if (success) {
                                constants.debugMessage('✓ Android device unregistered from notifications');
                            } else {
                                console.error('✗ Android device unregistration failed');
                            }
                            resolve(success);
                        } else {
                            // Late callback after timeout - log but don't process
                            console.warn('Android unregister callback received after timeout, ignoring');
                        }
                    };
                });

                window.AndroidBridge.unregisterFromNotifications();
                
                // Wait for unregister, then logout
                unregisterPromise.then(async () => await performLogout());
            } else {
                // Not Android, logout immediately
                await performLogout();
            }
        }
    }

    createAccount() {
        if (this.state.user_settings.logged_in) return
        this.runAction({
            actorId: "webforms.CreateUserRequestsWeb",
            action_full_name: "webforms.CreateUserRequests.insert"
        });
    }

    // handleVerification(action_name) {
    //     let pk = this.state.user_settings.user_id;
    //     this.data.user_state_change = true;
    //     this.runAction({actorId: "users.Me", action_full_name: action_name, [constants.URL_PARAM_SELECTED]: [pk]});
    // }

    addClass(element, className) {
        if (element.classList)
            element.classList.add(className);
        else
            element.className += ' ' + className;
    }

    removeClass(element, className) {
        if (element.classList)
            element.classList.remove(className);
        else
            element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    }

    isDesktop() {
        return window.innerWidth > 1024;
    }

    componentDidUpdate() {
        if (this.state.mobileMenuActive)
            this.addClass(document.body, 'body-overflow-hidden')
        else
            this.removeClass(document.body, 'body-overflow-hidden');
    }

    notification_web_socket(user_settings) {
        if (!window.Lino || !window.Lino.useWebSockets) return;

        let {user_id} = user_settings || this.state.user_settings;

        if (this.webSocketBridge) {
            this.webSocketBridge.close();
            this.setState({WS: false});
        }

        this.webSocketBridge = new this.ex.ReconnectingWebSocket.default(
            (window.location.protocol === "https:" ? "wss" : "ws") + "://" + window.location.host + "/WS/",
            [], // protocalls, not needed
            {} //options, see https://www.npmjs.com/package/reconnecting-websocket
        );

        // Helpful debugging
        this.webSocketBridge.addEventListener(
            'close', (e) => this.setState({WS: false}));

        // this.webSocketBridge.connect();
        this.webSocketBridge.addEventListener(
            'open', () => this.setState({WS: true}));


        this.webSocketBridge.addEventListener('message', (e) => {

            let data = JSON.parse(e.data);
            console.log("Received message ", data);
            if (data.type === constants.WSM_TYPE.NOTIFICATION) {
                this.push(data)
            } else if (data.type === constants.WSM_TYPE.CHAT) {
                console.warn("CHAT features has been disabled!");
            } else if (data.type === constants.WSM_TYPE.LIVE_PANEL_UPDATE) {
                const { panels } = this.URLContext.globals;
                Object.keys(data.data).filter(ID => ID in panels)
                    .forEach(ID => panels[ID].liveUpdate(data.data[ID]));
            }
        });
    }

    pushPermission() {
        let onGranted = () => console.log("onGranted");
        let onDenied = () => console.log("onDenied");
        // Ask for permission if it's not already granted
        Push.Permission.request(onGranted, onDenied);
    }

    push(data) {
        let {body, subject, action_url} = data;
        this.pushPermission();
        try {
            Push.create(subject, {
                body: body,
                icon: '/static/notify/lino-logo.png',
                onClick: function () {
                    window.open(action_url);
                }
            });
        }
        catch (err) {
            console.log(err.message);
        }

    }

    /**
     * Reset (as in reload) all content (children) of :js:class:`App`.
     *
     * When the :js:func:`~App.reset` is caused by a version mismatch
     * indexedDB and URI caches gets cleared.
     */
    async reset(signIn=false) {
        let LinoProgressBar = this.ex.sc.LinoProgressBar;
        this.setState({
            site_loading: true,
            site_data: null,
            menu_data: null,
            user_settings: null,
            initProgress: 10,
            children: <LinoProgressBar loading={true} progress={10}/>
        })
        this.setInitProgress(20);

        await this.syncAndroidColorSchemeToWindowGlobals();

        // reload with substitute user if su_id in URL doesn't match current substitute user
        const su_id = this.URLContext.value[constants.URL_PARAM_SUBST_USER];
        const color_scheme = this.URLContext.value[constants.URL_PARAM_COLOR_SCHEME];
        const shouldReloadForSubstUser = su_id != window.Lino.subst_user_id;
        const shouldReloadForColorScheme = ["light", "dark"].includes(color_scheme)
            && color_scheme !== window.Lino.color_scheme;
        if (shouldReloadForSubstUser || shouldReloadForColorScheme) {
            const url = new URL(window.location.href);
            if (this.URLContext.filled(su_id)) {
                url.searchParams.set(constants.URL_PARAM_SUBST_USER, su_id);
            } else {
                url.searchParams.delete(constants.URL_PARAM_SUBST_USER);
            }
            if (["light", "dark"].includes(color_scheme)) {
                url.searchParams.set(constants.URL_PARAM_COLOR_SCHEME, color_scheme);
            } else {
                url.searchParams.delete(constants.URL_PARAM_COLOR_SCHEME);
            }
            window.history.replaceState({}, '', url);
            window.location.reload();
            return
        }

        await this.fetch_user_settings(signIn);
        if (signIn) {
            // Check if we're running in Android WebView
            if (constants.isAndroidWebView()) {
                console.log('Android WebView detected, registering for notifications');

                // Define callback for registration result (optional)
                window.Android = window.Android || {};
                window.Android.onDeviceRegistered = function (success) {
                    if (success) {
                        constants.debugMessage('✓ Android device registered for push notifications');
                        // You can update UI to show notifications are enabled
                    } else {
                        console.error('✗ Failed to register Android device for notifications');
                        // You can show error message to user
                    }
                };

                // Call the Android registration method
                window.AndroidBridge.registerForNotifications();
            } else {
                constants.debugMessage('Not running in Android WebView, skipping Android notification registration');
                // Browser environment - use web push notifications or WebSockets
            }
        }
    }

    /**
     * Fetches settings for the logged in user or substitue user or anonymous
     */
    async fetch_user_settings(signIn=false) {
        this.setInitProgress(35);
        let url = "user/settings/", qs = {};
        this.URLContext.actionHandler.commonParams(qs);
        const user_settings = await this.URLContext.actionHandler.silentFetch({
            path: `${url}?${this.ex.queryString.stringify(qs)}`}
        )
        if (signIn && this.ex.i18n.language !== user_settings.user_lang) {
            constants.debugMessage(`Changing language to ${user_settings.user_lang} from ${this.ex.i18n.language}`);
            await this.ex.i18n.changeLanguage(user_settings.user_lang);
            /**
            * Call fetch_user_settings again with the new language preference
            */
            await this.fetch_user_settings(false);
            return;
        }
        this.setInitProgress(55);
        const { all } = this.ex.prLocale;
        if (Object.keys(all).includes(user_settings.locale)) {
            this.ex.prAPI.addLocale(user_settings.locale, all[user_settings.locale]);
            this.ex.prAPI.locale(user_settings.locale);
        }
        this.setState({
            user_settings,
            menu_data: this.createMenu(user_settings.user_menu),
            settings_key: `settings_${user_settings.site_name}`
        });

        this.notification_web_socket(user_settings);
        this.setInitProgress(70);
        await this.fetch_site_data(user_settings);
    };

    /**
     * Fetches siteData for the given URI.
     *
     * @param {Object} [data] user_settings.
     */
    async fetch_site_data(data) {
        this.setInitProgress(80);
        const key = getSiteDataKey(this.URLContext, data);
        const uri = data.site_data;
        const siteData = await this.URLContext.getSiteData(key, uri);
        this.setInitProgress(90);
        this.setState({
            site_data: siteData,
            site_loading: false,
            showOpenInAppBanner: this.shouldShowOpenInAppBanner(siteData),
            showFixAppLinksCta: this.shouldShowFixAppLinksCta(siteData),
        }, this.notifyAppLinkBannerLayoutChanged);
        if (siteData.theme_name && siteData.theme_name !== this.data.themeName) {
            this.data.themeName = siteData.theme_name;
            this.setTheme(this);
        }
        this.updatePushSubscription();
        const RootURLContext = this.ex.sc.RootURLContext;
        this.setState({children: <RootURLContext APP={this}/>});
        this.setInitProgress(100);
        if (constants.isAndroidWebView() && window.AndroidBridge?.hideLoadingOverlay) {
            try {
                window.AndroidBridge.hideLoadingOverlay();
            } catch (error) {
                console.warn('Failed to hide native Android loading overlay', error);
            }
        }
        if (this.state.zoomHandlerID) {
            clearInterval(this.state.zoomHandlerID);
            setTimeout(this.handleZoom, 1000);
        }
    };

    /**
     * When called, sets the :js:class:`App` children to :js:func:`InternalServerError`.
     */
    setServerError() {
        this.setState({children: <InternalServerError APP={this}/>, serverError: true});
    }

    /**
     * Hook for handling hard-coded (in HTML) action.
     *
     * @param {Object} [kwargs] see type ArgsRunAction
     */
    runAction(kwargs) {
        let aH, rp = kwargs[constants.URL_PARAM_REQUESTING_PANEL];
        if (rp && !rp.includes('dashboard') && !rp.includes('dItems'))
            aH = this.rps[kwargs[constants.URL_PARAM_REQUESTING_PANEL]];
        if (!aH) aH = this.URLContext.actionHandler;
        aH.checkAndRunAction(kwargs);
    };

    /**
     * Converts and returns sitedata menu data as Primereact menu data
     * with command functions
     *
     * @param {Object} [layout] siteData.menu
     * @returns {Object} menuData
     **/
    createMenu(layout) {
        // let counter = 0;
        //
        // const convert = (mi) => {
        //     let menu;
        //     if (!mi.text) {
        //         menu = "is_a_seperator";
        //     } else {
        //         let id = counter.toString();
        //         id += "_" + mi.text.replace(' ', '_');
        //         counter += 1;
        //         menu = {id: id, label: mi.text, command: (event) => {
        //             eval(mi.handler)}}
        //     }
        //     if (mi.menu && mi.menu.items) {
        //         menu.items = mi.menu.items.map(convert);
        //         delete menu.command; // Only have command on submenu items,
        //     }
        //     return menu;
        // };
        // let result = layout.map(convert);
        // this.data.miStore = result;
        // return result
        let miStore = [];
        this.data.counter = [0];

        const convert = (mi, store) => {
            let menu, storeMenu = {};
            if (!mi.text) {
                menu = "is_a_seperator";
            } else {
                let id = this.data.counter[0].toString();
                id += "_" + mi.text;
                id = id.replaceAll(' ', '_');
                id = id.replaceAll("(", "_");
                id = id.replaceAll(")", "_");
                id = id.replaceAll(".", "_");
                this.data.counter[0] += 1;
                menu = {
                    id: "m" + id,
                    label: mi.text,
                    command: (event) => {
                        eval(mi.handler);
                    }
                }
            };
            if (typeof menu !== 'string') storeMenu.id = menu.id;
            if (mi.menu && mi.menu.items) {
                storeMenu.items= [];
                menu.items = mi.menu.items.map(mi => convert(mi, storeMenu.items));
                delete menu.command; // Only have command on submenu items,
            }
            if (Object.keys(storeMenu).length) store.push(storeMenu);
            return menu;
        };
        let result = layout.map(mi => convert(mi, miStore));
        if (this.data.counter) delete this.data.counter;
        this.data.miStore = miStore;
        return result
    }

    getSettings() {
        return window.localStorage.getObject(this.state.settings_key) || {};
    }

    setSettings(settings) {
        window.localStorage.setObject(this.state.settings_key,
            Object.assign(this.getSettings(), settings));
    }

    async reload() {
        // const su_id = this.URLContext.value[constants.URL_PARAM_SUBST_USER];

        await this.URLContext.iDBclear();
        window.localStorage.clear();

        // if (this.URLContext.filled(su_id)) {
        //     const url = new URL(window.location.href);
        //     url.searchParams.set(constants.URL_PARAM_SUBST_USER, su_id);
        //     window.history.replaceState({}, '', url);
        // }
        const url = new URL(window.location.href);
        url.searchParams.set("timestamp", Date.now().toString());
        window.history.replaceState({}, '', url);
        window.location.reload();
    }

    render() {
        if (!this.state.ready) return null;
        const appLinkOptions = this.getAppLinkOptions();
        return <React.Fragment>
            {this.state.showOpenInAppBanner &&
                <div className="l-app-link-banner" style={{padding: '10px', textAlign: 'center', borderBottom: '1px solid #ddd'}}>
                    <strong>{appLinkOptions.open_title}</strong> {appLinkOptions.open_message}{' '}
                    <button type="button" onClick={this.openInAppFromBrowser}>{appLinkOptions.open_button_label}</button>{' '}
                    <button type="button" onClick={this.dismissOpenInAppBanner}>{appLinkOptions.dismiss_button_label}</button>
                </div>}
            {this.state.showFixAppLinksCta &&
                <div className="l-app-link-banner" style={{padding: '10px', textAlign: 'center', borderBottom: '1px solid #ddd'}}>
                    {appLinkOptions.fix_message}{' '}
                    <button type="button" onClick={this.openAndroidAppLinkSettings}>{appLinkOptions.fix_button_label}</button>{' '}
                    <button type="button" onClick={this.dismissFixAppLinksCta}>{appLinkOptions.dismiss_button_label}</button>
                </div>}
            {this.state.loadMask && <this.ex.sc.LinoLoadMask/>}
            <this.ex.prToast.Toast ref={(el) => this.toast = el}/>

            {this.state.children}

            <div className="layout-mask"/>
            <iframe id="temp" name="temp" style={{display: "none"}}/>
            <this.ex.sc.DialogFactory ref={ref => this.dialogFactory = ref} APP={this}/>
        </React.Fragment>
    }
}
export {App};


export function Main() {
    const localEx = getExReady(ex, ["rrd"]);
    return !localEx.ready ? null : <localEx.rrd.HashRouter>
        <LinoRouter RRD={localEx.rrd}/>
    </localEx.rrd.HashRouter>
}


export async function render() {
    const rootElement = document.getElementById("root");
    if (rootElement) {
        // Populate LinoUtils with the modules it require.
        pushExternalModules(await ex.resolve(["queryString", "weakKey"]));

        // render app.
        const rdc = (await ex.resolve(["rdc"])).rdc;
        rdc.createRoot(rootElement).render(<Main/>);
    }
}
